import { cn } from "@newxsystems/shared";
import { getComponent } from "../../../../client";
import type { IDaylightActionProps } from "./DaylightAction.props";

/**
 * The frozen action matrix (DESIGN.md §5). Keys mirror the CMS `style` select exactly:
 * primary | secondary | ghost | destructive. Anything else falls back to primary so a stale
 * or unknown value can never render an unstyled control.
 */
const styles = {
  primary:
    "border-primary bg-primary text-primary-foreground hover:border-primary-700 hover:bg-primary-700",
  secondary:
    "border-border bg-transparent text-foreground hover:border-primary hover:bg-primary hover:text-primary-foreground",
  ghost: "border-transparent bg-transparent text-primary hover:text-graphite",
  destructive:
    "border-destructive bg-destructive text-destructive-foreground hover:bg-destructive/90",
} as const;

type ActionStyle = keyof typeof styles;

export const DaylightAction = ({ action, className }: IDaylightActionProps) => {
  const Link = getComponent("Link");
  const variant = styles[action.style as ActionStyle] ?? styles.primary;
  return (
    <Link
      aria-label={action.accessibilityLabel || undefined}
      className={cn(
        "group inline-flex min-h-11 items-center justify-center gap-2 border px-4 py-2 daylight-label transition-[background-color,border-color,color] duration-200 ease-out focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring",
        variant,
        className,
      )}
      href={action.href}
      rel={action.openInNewTab ? "noopener noreferrer" : undefined}
      target={action.openInNewTab ? "_blank" : undefined}
    >
      <span>{action.label}</span>
      <span
        aria-hidden="true"
        className="transition-transform duration-200 ease-out group-hover:translate-x-1"
      >
        →
      </span>
    </Link>
  );
};

DaylightAction.displayName = "Content::DaylightAction";
